import { EntityType } from '@/components/entities/entityType';
import ErrorMessage from '@/components/errorMessage';
import { EventPayloadViewer, JsonViewer } from '@/components/viewers';
import { useApiContext } from '@/contexts/apiContext';
import { EventDataItemDto, ExecutionStep, PipelineExecutionState } from '@/interfaces/pipelineExecution.interface';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Chip, Collapse, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import Markdown from 'markdown-to-jsx';
import { useEffect, useState } from 'react';

const StemItem: React.FC<{
  pipelineExecutionState?: PipelineExecutionState;
  step: ExecutionStep;
  item: EventDataItemDto;
  itemResponse?: EventDataItemDto;
  onRestartFromPoint?: (step: ExecutionStep, eventDataId?: string | number) => void;
}> = ({ pipelineExecutionState, item, step, itemResponse, onRestartFromPoint }) => {
  const apiContext = useApiContext();

  const [open, setOpen] = useState(false);
  const [eventsData, setEventsData] = useState<any[]>([]);

  const isFailed =
    item.status?.toLocaleLowerCase() === 'failed' ||
    item.status?.toLocaleLowerCase() === 'error' ||
    Boolean(item.error);

  useEffect(() => {
    const getPipelineStepItemEventsData = async () => {
      try {
        const eventsData =
          step.entityType === EntityType.dataSourceInstance
            ? await apiContext.getDataSourceItemEventsData(
                step.entityDefId,
                step.entityId,
                item.dataSourceItemId || item.dataSourceItemGroup || item.id,
                true,
                pipelineExecutionState!.pipelineInstanceId,
              )
            : step.entityType === EntityType.agentInstance
              ? await apiContext.getAgentInstanceEventsData(
                  step.entityDefId,
                  step.entityId,
                  item.dataSourceDefId,
                  item.dataSourceInstanceId,
                  item.dataSourceItemId || item.dataSourceItemGroup || item.id,
                  pipelineExecutionState?.pipelineInstanceId,
                )
              : await apiContext.getDataSourceItemEventsData(
                  step.entityDefId,
                  step.entityId,
                  item.dataSourceItemId || item.dataSourceItemGroup || item.id,
                  false,
                  pipelineExecutionState!.pipelineInstanceId,
                );
        setEventsData(eventsData || []);
      } catch (err) {
        console.error('Error fetching step item events data:', err);
      }
    };
    if (step && pipelineExecutionState) {
      getPipelineStepItemEventsData();
    }
  }, [step, pipelineExecutionState]);

  const payload = (item as any).eventPayload ?? (item as any).result;

  return (
    <>
      <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ py: 0.75, width: 40 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ py: 0.75 }}>
          {(() => {
            const friendlyName =
              (item as any).itemName ||
              (typeof payload === 'object' &&
                payload &&
                (payload.itemName || payload.fileName || payload.name || payload.title || payload.documentName));
            return (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {friendlyName ||
                    (item.dataSourceItemId ? `Item ID: ${item.dataSourceItemId}` : `Event ID: ${item.id}`)}
                </Typography>
                {friendlyName && item.dataSourceItemId && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Item ID: {item.dataSourceItemId}
                  </Typography>
                )}
                {item.dataSourceItemGroup && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Group: {item.dataSourceItemGroup}
                  </Typography>
                )}
                {itemResponse?.error && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#b91c1c',
                        bgcolor: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.25,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontWeight: 600,
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                      }}
                    >
                      <ErrorIcon sx={{ fontSize: 13, color: '#dc2626' }} />
                      {itemResponse.error}
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}
        </TableCell>
        <TableCell sx={{ py: 0.75 }}>
          {(() => {
            const upstreamEntityName = item.lineage?.upstreamEntityName;
            const eventName = item.lineage?.upstreamEventName;

            if (upstreamEntityName) {
              return (
                <Tooltip title={`Triggered by ${upstreamEntityName} (${eventName})`}>
                  <Chip
                    label={`${upstreamEntityName} • ${eventName}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: '#f1f5f9',
                      color: '#1e293b',
                      fontWeight: 600,
                      maxWidth: 180,
                      '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                  />
                </Tooltip>
              );
            }

            return (
              <Typography variant="caption" color="text.secondary">
                Root Ingestion
              </Typography>
            );
          })()}
        </TableCell>
        <TableCell sx={{ py: 0.75 }}>
          <Tooltip title={item.error ? `Error: ${item.error}` : isFailed ? 'Failed' : 'Success'}>
            <Chip
              icon={
                isFailed ? (
                  <ErrorIcon sx={{ fontSize: '14px !important' }} />
                ) : (
                  <CheckCircleIcon sx={{ fontSize: '14px !important' }} />
                )
              }
              label={isFailed ? 'Failed' : 'Success'}
              color={isFailed ? 'error' : 'success'}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Stack spacing={1.25}>
                {item.error && (
                  <Box sx={{ mb: 1 }}>
                    <ErrorMessage error={{ message: `Item Processing Error: ${item.error}` }} />
                  </Box>
                )}

                {(item as any).result && (
                  <EventPayloadViewer payload={(item as any).result} label="Processing Result" showMimeType={true} />
                )}
                {eventsData.length > 0 ? (
                  eventsData.map((eventData, eIdx) => {
                    const isEventError =
                      eventData.error || Boolean(eventData.eventName?.toLowerCase().includes('error'));
                    const inputEvent = eventData.inputEvent || (item as any).inputEvent;

                    return (
                      <Box
                        key={eventData.id || eIdx}
                        sx={{
                          p: 1.25,
                          bgcolor: '#fff',
                          borderRadius: 1,
                          border: '1px solid #cbd5e1',
                        }}
                      >
                        <Stack spacing={1.25}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={eventData.eventName || 'Event'}
                                size="small"
                                color={isEventError ? 'error' : 'primary'}
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                              />
                              {eventData.status && (
                                <Chip
                                  label={eventData.status}
                                  size="small"
                                  color={eventData.status === 'failed' || isEventError ? 'error' : 'success'}
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Stack>
                            {eventData.dateTime && (
                              <Typography variant="caption" color="text.secondary">
                                {new Date(eventData.dateTime).toLocaleString()}
                              </Typography>
                            )}
                          </Stack>

                          {eventData.error && <ErrorMessage error={eventData.error} />}

                          {/* Input Event Section */}
                          {inputEvent &&
                            (() => {
                              const inputEventName =
                                inputEvent.eventName ||
                                (inputEvent.triggerType ? `Trigger: ${inputEvent.triggerType}` : 'Event');
                              const sourceEntityType =
                                inputEvent.sourceEntityType ||
                                (inputEvent.triggerType ? 'dataSourceInstance' : item.lineage?.upstreamEntityType);
                              const sourceEntityId =
                                inputEvent.sourceEntityId ||
                                inputEvent.sourceEntityDefId ||
                                item.lineage?.upstreamEntityId ||
                                item.dataSourceInstanceId ||
                                (item as any).dataSourceId;
                              const sourceEntityName =
                                inputEvent.sourceEntityName ||
                                inputEvent.sourceEntityDefName ||
                                item.lineage?.upstreamEntityName;
                              const inputEventTimestamp =
                                inputEvent.timestamp || inputEvent.dateTime || eventData.dateTime || item.dateTime;

                              return (
                                <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    gap={1}
                                  >
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 700, color: '#334155', display: 'inline-block' }}
                                      >
                                        Triggering Input Event:
                                      </Typography>
                                      <Chip
                                        label={inputEventName}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                      />
                                      {sourceEntityType && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ fontSize: '0.75rem' }}
                                        >
                                          Source: <strong>{sourceEntityType}</strong>
                                          {sourceEntityId && (
                                            <span style={{ color: '#475569', marginLeft: 4 }}>
                                              (ID: <code>{sourceEntityId}</code>)
                                            </span>
                                          )}
                                          {sourceEntityName && ` • ${sourceEntityName}`}
                                        </Typography>
                                      )}
                                    </Stack>
                                    {inputEventTimestamp && (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {new Date(inputEventTimestamp).toLocaleString()}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Box>
                              );
                            })()}

                          {/* Output Event Payload Section */}
                          <EventPayloadViewer
                            payload={eventData.eventPayload}
                            label="Output Event Payload"
                            showMimeType={true}
                          />
                        </Stack>
                      </Box>
                    );
                  })
                ) : (
                  <>
                    {(item as any).inputEvent &&
                      (() => {
                        const inputEvent = (item as any).inputEvent;
                        const inputEventName =
                          inputEvent.eventName ||
                          (inputEvent.triggerType ? `Trigger: ${inputEvent.triggerType}` : 'Event');
                        const sourceEntityType =
                          inputEvent.sourceEntityType ||
                          (inputEvent.triggerType ? 'dataSourceInstance' : item.lineage?.upstreamEntityType);
                        const sourceEntityId =
                          inputEvent.sourceEntityId ||
                          inputEvent.sourceEntityDefId ||
                          item.lineage?.upstreamEntityId ||
                          item.dataSourceInstanceId ||
                          (item as any).dataSourceId;
                        const sourceEntityName =
                          inputEvent.sourceEntityName ||
                          inputEvent.sourceEntityDefName ||
                          item.lineage?.upstreamEntityName;
                        const inputEventTimestamp =
                          inputEvent.timestamp || inputEvent.dateTime || item.dateTime || (item as any).lastScannedDate;

                        return (
                          <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              flexWrap="wrap"
                              gap={1}
                            >
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 700, color: '#334155', display: 'inline-block' }}
                                >
                                  Triggering Input Event:
                                </Typography>
                                <Chip
                                  label={inputEventName}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                />
                                {sourceEntityType && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    Source: <strong>{sourceEntityType}</strong>
                                    {sourceEntityId && (
                                      <span style={{ color: '#475569', marginLeft: 4 }}>
                                        (ID: <code>{sourceEntityId}</code>)
                                      </span>
                                    )}
                                    {sourceEntityName && ` • ${sourceEntityName}`}
                                  </Typography>
                                )}
                              </Stack>
                              {inputEventTimestamp && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {new Date(inputEventTimestamp).toLocaleString()}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        );
                      })()}
                    {item.eventPayload && (
                      <Box sx={{ p: 1, bgcolor: '#fff', borderRadius: 1, border: '1px solid #cbd5e1' }}>
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}
                        >
                          Output Event Payload:
                        </Typography>
                        {typeof item.eventPayload === 'object' ? (
                          <JsonViewer json={item.eventPayload} maxHeight={0} />
                        ) : (
                          <Markdown>{String(item.eventPayload)}</Markdown>
                        )}
                      </Box>
                    )}
                    {!item.error &&
                      !(item as any).result &&
                      !(item as any).inputEvent &&
                      !item.eventPayload &&
                      !(item as any).itemName && (
                        <Typography variant="caption" color="text.secondary">
                          No event records found for this item.
                        </Typography>
                      )}
                  </>
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default StemItem;
